import {
  Box,
  Button,
  Flex,
  Image,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { forwardRef, ReactNode, useState } from 'react';

import {
  FuelReading,
  FuelReceiptCheck,
  FuelSlot,
  FuelSummary,
  FuelSummaryPumpDay,
  fullPhotoUrl,
} from '../../../services/fuelReadingsService';
import {
  WEEKDAYS,
  differenceLevel,
  formatDateTime,
  formatLiters,
  formatReading,
  isoDay,
  monthLabel,
  sheetTotals,
} from '../../../utils/fuelReadings';

const OCCURRENCES_SHOWN = 2; // na tela; o resto abre no "ver mais" (no papel vão todas)

// ocorrências do dia numa coluna estreita: as primeiras e um "ver mais"
function Occurrences({ items, print }: { items: string[]; print: boolean }) {
  const [open, setOpen] = useState(false);
  const shown = print || open ? items : items.slice(0, OCCURRENCES_SHOWN);
  const hidden = items.length - OCCURRENCES_SHOWN;

  return (
    <>
      {shown.map(text => (
        <Text key={text} mb={print ? '0' : '1'}>
          {text}
        </Text>
      ))}
      {!print && hidden > 0 && (
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={() => setOpen(!open)}
        >
          {open ? 'ver menos' : `+ ${hidden} (ver mais)`}
        </Button>
      )}
    </>
  );
}

interface DieselSheetProps {
  summary: FuelSummary;
  pumpIds: string[];
  month: string; // yyyy-mm
  readings?: FuelReading[]; // para abrir a foto ao clicar no número
  onPhoto?: (reading: FuelReading) => void;
  print?: boolean; // versão em papel: preto no branco, com as assinaturas
}

// a folha "controle diesel" da controladoria, preenchida pelo sistema:
// leitura 7h e 17h de cada bomba, aferição, total bombeado e o gescooper.
export const DieselSheet = forwardRef<HTMLDivElement, DieselSheetProps>(
  ({ summary, pumpIds, month, readings = [], onPhoto, print = false }, ref) => {
    const today = isoDay(new Date());
    const totals = sheetTotals(summary.days, pumpIds);
    const pumpName = (id: string) =>
      summary.pumps.find(pump => pump.id === id)?.name || 'Bomba';
    const line = print ? '#999' : 'gray.700';
    const muted = print ? '#555' : 'gray.500';
    const warn = print ? '#b45309' : 'orange.400';
    const cell = {
      px: print ? '1' : '2',
      py: print ? '0.5' : '1.5',
      borderColor: line,
      borderWidth: '1px',
      fontSize: print ? '9px' : 'sm',
      color: print ? '#000' : undefined,
    };
    const head = {
      ...cell,
      textAlign: 'center' as const,
      textTransform: 'none' as const,
      letterSpacing: 'normal',
      fontSize: print ? '9px' : 'xs',
      color: print ? '#000' : 'gray.400',
      bg: print ? '#eee' : 'gray.900',
    };

    // situação da nf anotada na descarga no gescooper
    const receiptStatus = (item: FuelReceiptCheck) => {
      if (item.erp_day === null) {
        return 'sem entrada';
      }

      return item.late_days
        ? `entrou ${item.erp_day.slice(8, 10)}/${item.erp_day.slice(5, 7)}`
        : 'ok';
    };

    const differenceColor = (level: string) => {
      if (level === 'warning') {
        return warn;
      }

      return level === 'ok' && !print ? 'green.400' : cell.color;
    };

    const photoOf = (pumpId: string, slot: FuelSlot, day: string) =>
      readings.find(
        item =>
          !item.canceled_at &&
          item.pump_id === pumpId &&
          item.slot === slot &&
          item.read_at.startsWith(day),
      );

    // todas as células de leitura no mesmo padrão: o número em cima e, embaixo,
    // a foto pequena (clicando abre) ou a etiqueta "sem foto" (ex.: lançada no
    // pc do posto, foto ainda não anexada). faltando: "falta". liberado pelo
    // supervisor/ti (sem número): "liberado", com o motivo no title
    const readingCell = (
      row: FuelSummaryPumpDay | undefined,
      slot: FuelSlot,
      day: string,
    ): ReactNode => {
      const value = slot === 'abertura' ? row?.opening : row?.closing;

      if (value === null || value === undefined) {
        const release = readings.find(
          item =>
            item.reading === null &&
            !item.canceled_at &&
            item.pump_id === row?.pump_id &&
            item.slot === slot &&
            item.read_at.startsWith(day),
        );

        if (release) {
          return (
            <Text as="span" color="blue.300" title={release.notes || ''}>
              liberado
            </Text>
          );
        }

        return row?.missing.includes(slot) ? (
          <Text as="span" color={warn}>
            falta
          </Text>
        ) : null;
      }

      const overnight = slot === 'abertura' && (row?.overnight ?? 0) > 0.5;
      const reading =
        !print && onPhoto ? photoOf(row?.pump_id || '', slot, day) : undefined;
      const at = slot === 'abertura' ? row?.opening_at : row?.closing_at;
      const text = (
        <Text
          as="span"
          color={overnight ? warn : undefined}
          title={
            overnight
              ? `${formatLiters(
                  row?.overnight,
                )} L a mais que o fechamento anterior`
              : `lançada ${formatDateTime(at || null)}`
          }
        >
          {formatReading(value)}
          {overnight && ' *'}
        </Text>
      );

      if (reading?.photo_url) {
        return (
          <Box
            as="button"
            type="button"
            onClick={() => onPhoto?.(reading)}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="1"
            w="100%"
            aria-label={`Ver a foto da leitura ${formatReading(value)}`}
          >
            {text}
            <Image
              src={fullPhotoUrl(reading.photo_url) || ''}
              alt=""
              boxSize="28px"
              objectFit="cover"
              borderRadius="4px"
              loading="lazy"
            />
          </Box>
        );
      }

      if (reading) {
        return (
          <Flex direction="column" align="center" gap="1">
            {text}
            <Tag
              size="sm"
              colorScheme="orange"
              title={reading.notes || 'Leitura sem foto: dá para anexar depois'}
            >
              sem foto
            </Tag>
          </Flex>
        );
      }

      return text;
    };

    return (
      <Box
        ref={ref}
        bg={print ? '#fff' : undefined}
        color={print ? '#000' : undefined}
        p={print ? '2' : '0'}
      >
        <Flex
          justify="space-between"
          align="baseline"
          mb="2"
          wrap="wrap"
          gridGap="2"
        >
          <Text fontWeight="bold" fontSize={print ? '13px' : 'lg'}>
            CONTROLE DIESEL (8607) – MÊS: {monthLabel(month)}
          </Text>
          <Text fontSize={print ? '9px' : 'xs'} color={muted}>
            Total bombeado = leitura 17h − leitura 7h − aferição (sábado:
            fechamento às 11h). Diferença = total bombeado − saídas no sistema.
          </Text>
        </Flex>

        <Box overflowX={print ? 'visible' : 'auto'}>
          <Table size="sm" sx={{ borderCollapse: 'collapse' }}>
            <Thead>
              <Tr>
                <Th {...head} rowSpan={2}>
                  Data
                </Th>
                {pumpIds.map(id => (
                  <Th {...head} key={id} colSpan={4}>
                    Leitura {pumpName(id)}
                  </Th>
                ))}
                <Th {...head} colSpan={2}>
                  Entrada de diesel (descarga)
                </Th>
                <Th {...head} colSpan={3}>
                  Sistema (GesCooper)
                </Th>
                <Th {...head} rowSpan={2}>
                  Diferença (L)
                </Th>
                <Th {...head} rowSpan={2} w={print ? 'auto' : '240px'}>
                  Ocorrências
                </Th>
              </Tr>
              <Tr>
                {pumpIds.map(id => [
                  <Th {...head} key={`${id}-7`}>
                    Leitura 7h00
                  </Th>,
                  <Th {...head} key={`${id}-17`}>
                    Leitura 17h
                  </Th>,
                  <Th {...head} key={`${id}-af`}>
                    Aferição (L)
                  </Th>,
                  <Th {...head} key={`${id}-tot`}>
                    Total bombeado dia (L)
                  </Th>,
                ])}
                <Th {...head}>Entrada NF (L)</Th>
                <Th {...head}>Nº NF</Th>
                <Th {...head}>Entrada (L)</Th>
                <Th {...head}>Nº NF</Th>
                <Th {...head}>Saídas no sistema (L)</Th>
              </Tr>
            </Thead>
            <Tbody>
              {summary.days.map(day => {
                const future = day.day > today;
                const level = differenceLevel(day.difference, day.erp_out);
                const quiet = day.weekday === 0 || day.closed;

                return (
                  <Tr
                    key={day.day}
                    bg={!print && quiet ? 'gray.900' : undefined}
                  >
                    <Td {...cell} whiteSpace="nowrap">
                      {day.day.slice(8, 10)}{' '}
                      <Text as="span" color={muted}>
                        {WEEKDAYS[day.weekday]}
                      </Text>
                    </Td>
                    {pumpIds.map(id => {
                      const row = day.pumps.find(item => item.pump_id === id);

                      return [
                        <Td
                          {...cell}
                          key={`${id}-7`}
                          textAlign="center"
                          fontFamily="mono"
                          whiteSpace="nowrap"
                        >
                          {readingCell(row, 'abertura', day.day)}
                        </Td>,
                        <Td
                          {...cell}
                          key={`${id}-17`}
                          textAlign="center"
                          fontFamily="mono"
                          whiteSpace="nowrap"
                        >
                          {readingCell(row, 'fechamento', day.day)}
                        </Td>,
                        <Td {...cell} key={`${id}-af`} isNumeric>
                          {row?.calibration_liters
                            ? formatLiters(row.calibration_liters)
                            : ''}
                        </Td>,
                        <Td
                          {...cell}
                          key={`${id}-tot`}
                          isNumeric
                          fontWeight="bold"
                        >
                          {row?.net === null || row?.net === undefined
                            ? ''
                            : formatLiters(row.net)}
                        </Td>,
                      ];
                    })}
                    <Td {...cell} isNumeric>
                      {day.in_liters ? formatLiters(day.in_liters) : ''}
                    </Td>
                    {/* como nas leituras: o nº da nf em cima e a situação embaixo */}
                    <Td {...cell} textAlign="center">
                      {day.receipts.map(item => (
                        <Box
                          key={item.id}
                          whiteSpace="nowrap"
                          color={item.erp_day === null ? warn : undefined}
                        >
                          <Text>{item.document}</Text>
                          <Text
                            color={item.erp_day === null ? warn : muted}
                            fontSize="0.85em"
                          >
                            {receiptStatus(item)}
                          </Text>
                        </Box>
                      ))}
                    </Td>
                    <Td {...cell} isNumeric>
                      {day.erp_in ? formatLiters(day.erp_in) : ''}
                    </Td>
                    <Td {...cell}>{day.erp_in_documents.join(', ')}</Td>
                    <Td {...cell} isNumeric>
                      {!future && day.erp_out ? formatLiters(day.erp_out) : ''}
                    </Td>
                    <Td
                      {...cell}
                      isNumeric
                      fontWeight="bold"
                      color={differenceColor(level)}
                      title={
                        !future && !day.complete && day.erp_out
                          ? 'Faltam leituras das bombas para conciliar'
                          : undefined
                      }
                    >
                      {day.difference !== null && formatLiters(day.difference)}
                      {day.difference === null &&
                        !future &&
                        !day.complete &&
                        !!day.erp_out &&
                        '…'}
                    </Td>
                    <Td
                      {...cell}
                      fontSize={print ? '8px' : 'xs'}
                      minW={print ? '0' : '240px'}
                      maxW={print ? 'none' : '240px'}
                      whiteSpace="normal"
                      lineHeight="short"
                    >
                      <Occurrences items={day.occurrences} print={print} />
                    </Td>
                  </Tr>
                );
              })}
              <Tr fontWeight="bold" bg={print ? '#eee' : 'gray.900'}>
                <Td {...cell} fontWeight="bold">
                  TOTAL
                </Td>
                {pumpIds.map(id => [
                  <Td {...cell} key={`${id}-7`} />,
                  <Td {...cell} key={`${id}-17`} />,
                  <Td {...cell} key={`${id}-af`} isNumeric fontWeight="bold">
                    {formatLiters(totals.pumps[id].calibration)}
                  </Td>,
                  <Td {...cell} key={`${id}-tot`} isNumeric fontWeight="bold">
                    {formatLiters(totals.pumps[id].net)}
                  </Td>,
                ])}
                <Td {...cell} isNumeric fontWeight="bold">
                  {formatLiters(totals.inLiters)}
                </Td>
                <Td {...cell}>
                  {totals.documents > 0 && `${totals.documents} NF`}
                </Td>
                <Td {...cell} isNumeric fontWeight="bold">
                  {formatLiters(totals.erpIn)}
                </Td>
                <Td {...cell} />
                <Td {...cell} isNumeric fontWeight="bold">
                  {formatLiters(totals.erpOut)}
                </Td>
                <Td {...cell} isNumeric fontWeight="bold">
                  {formatLiters(totals.difference)}
                </Td>
                <Td {...cell} />
              </Tr>
            </Tbody>
          </Table>
        </Box>

        {print && (
          <Flex justify="space-around" mt="10" fontSize="10px">
            <Box textAlign="center" w="35%">
              <Box borderTop="1px solid #000" pt="1">
                Colaborador
              </Box>
            </Box>
            <Box textAlign="center" w="35%">
              <Box borderTop="1px solid #000" pt="1">
                Encarregado
              </Box>
            </Box>
          </Flex>
        )}
      </Box>
    );
  },
);

DieselSheet.displayName = 'DieselSheet';
